/**
 * Pílula flutuante "Nova versão disponível · Atualizar" (design da tela de
 * entrada, out/2026 — a mesma pílula do PharuCut).
 *
 * Aparece em qualquer tela do ERP quando sai um deploy novo e esta aba ainda
 * roda o anterior: embaixo e centralizada no celular, no canto inferior
 * esquerdo a partir de 1100 px. Atualizar recarrega a página na versão nova;
 * o X dispensa o aviso (a pessoa pode estar no meio de um formulário). Não
 * aparece nas páginas públicas que o CLIENTE abre (orçamento, contrato,
 * entrega, pesquisa, peça, formulário).
 *
 * Forma, tamanhos, sombra e blur são os do design; as cores vêm dos tokens do
 * tema do app (--surface, --border-strong, --accent, --ink…) para casar com
 * qualquer tela, clara ou escura.
 */
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

import useVersaoNova from "../../hooks/useVersaoNova";

const PAGINAS_DO_CLIENTE = /^\/(p|f|convite)\//;
// Telas de entrada (design "pharu-erp-login"): lá a pílula usa a paleta do
// próprio design, não o destaque do tema do app (que pode ser laranja).
const TELAS_DE_ENTRADA = /^\/(login|mfa-verify)(\/|$)/;

/** Cores exatas do .toast do design de login (claro e escuro). */
const paletaDaEntrada = css`
  --accent: #0369a1;
  --ink-on-accent: #ffffff;
  --border-strong: #d2d9e3;
  --ink: #0e1422;
  --ink-3: #4a5468;
  --surface-2: #eef1f5;
  background: rgba(255, 255, 255, 0.96);

  [data-theme="dark"] & {
    --accent: #38bdf8;
    --ink-on-accent: #03131f;
    --border-strong: #2a3244;
    --ink: #eef1f6;
    --ink-3: #9aa3b5;
    --surface-2: #141824;
    background: rgba(20, 24, 36, 0.94);
  }
`;

const subir = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const girar = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Pilula = styled.div`
  position: fixed;
  z-index: 1200;
  left: 0;
  right: 0;
  bottom: calc(16px + var(--safe-area-bottom, 0px));
  margin: 0 auto;
  width: max-content;
  max-width: calc(100% - 32px);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px;
  border: 1px solid var(--border-strong, var(--border));
  border-radius: 999px;
  background: color-mix(in srgb, var(--surface) 94%, transparent);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  box-shadow: 0 20px 40px -18px rgba(15, 23, 42, 0.35);
  color: var(--ink);
  font: 500 13px/1 var(--font-body, "Sora", sans-serif);
  white-space: nowrap;

  [data-theme="dark"] & {
    box-shadow: 0 20px 40px -16px rgba(0, 0, 0, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }

  ${({ $entrada }) => $entrada && paletaDaEntrada}

  @media (prefers-reduced-motion: no-preference) {
    animation: ${subir} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }

  /* Celular: acima das abas de rodapé que algumas telas têm. */
  @media (max-width: 600px) {
    bottom: calc(76px + var(--safe-area-bottom, 0px));
  }

  @media (min-width: 1100px) {
    left: 24px;
    right: auto;
    bottom: calc(24px + var(--safe-area-bottom, 0px));
    margin: 0;
  }
`;

const Icone = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
`;

const Texto = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 2px;
`;

const Atualizar = styled.button`
  flex: none;
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: var(--accent);
  color: var(--ink-on-accent);
  font: 600 13px var(--font-body, "Sora", sans-serif);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const Girando = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid color-mix(in srgb, var(--ink-on-accent) 30%, transparent);
  border-top-color: var(--ink-on-accent);
  border-radius: 50%;
  animation: ${girar} 0.7s linear infinite;
`;

const Fechar = styled.button`
  flex: none;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-3);
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
    color: var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

function IconeBaixar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
    </svg>
  );
}

function IconeFechar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

export default function AvisoDeVersaoNova({ ativo }) {
  const { pathname } = useLocation();
  const { disponivel, dispensar, atualizar } = useVersaoNova(ativo === undefined ? undefined : { ativo });
  const [atualizando, setAtualizando] = useState(false);

  if (!disponivel || PAGINAS_DO_CLIENTE.test(pathname)) return null;

  return (
    <Pilula role="status" aria-live="polite" $entrada={TELAS_DE_ENTRADA.test(pathname)}>
      <Icone>
        <IconeBaixar />
      </Icone>
      <Texto>Nova versão disponível</Texto>
      <Atualizar
        type="button"
        disabled={atualizando}
        onClick={() => {
          setAtualizando(true);
          atualizar();
        }}
      >
        {atualizando && <Girando aria-hidden="true" />}
        {atualizando ? "Atualizando…" : "Atualizar"}
      </Atualizar>
      <Fechar type="button" aria-label="Dispensar aviso" title="Dispensar aviso" onClick={dispensar}>
        <IconeFechar />
      </Fechar>
    </Pilula>
  );
}
